import { memo, ReactNode, useLayoutEffect } from 'react';

import useIsMounted from '@utils/hooks/isMounted';

import { getDevice } from '../../device/getDevice';
import { sasicQueuePush } from '../Sasic';
import { SasicAction } from '../SasicTypes';

export interface SasicAdvertProps {
  advertKey?: string | number;
  elementId: string;
  groupId?: string;
  className?: string;
  type?: string;
  supertag?: string;
  pos?: string | number;
  children?: ReactNode;
}

const SasicAdvert = ({
  elementId,
  groupId = 'default',
  className,
  children,
  supertag,
  pos = 1,
  advertKey,
}: SasicAdvertProps) => {
  useLayoutEffect(() => {
    const callback = (event: { type: string; is_empty: boolean }) => {
      if (event.type === 'insert' && !event.is_empty) {
        const advertLabelContainerNode = document.getElementById(elementId)
          ?.parentNode?.firstChild as HTMLElement;

        if (advertLabelContainerNode) {
          advertLabelContainerNode.style.display = 'block';
        }
      }
    };

    const options = {
      pos,
      targets: {
        supertag,
        device: getDevice(),
      },
      callback,
    };

    sasicQueuePush([
      SasicAction.POSITION,
      { elementId, groupId, options, reenableHb: true },
    ]);

    return () => {
      sasicQueuePush([SasicAction.REMOVE_POSITION, elementId]);
    };
  }, [elementId, groupId, pos, supertag]);

  useLayoutEffect(() => {
    sasicQueuePush([
      SasicAction.LOAD_POSITION,
      { elementId, groupId, reenableHb: true },
    ]);
  }, [advertKey, elementId, groupId]);

  return (
    <div id={elementId} className={className}>
      {children}
    </div>
  );
};

const SasicAdvertWrapper = (props: SasicAdvertProps) => {
  const isMounted = useIsMounted();

  if (isMounted) {
    return <SasicAdvert {...props} />;
  }

  return null;
};

export default memo(
  SasicAdvertWrapper,
  (previousProps, nextProps) => previousProps.advertKey === nextProps.advertKey,
);
